import { useEffect, useMemo } from 'react';
import _ from 'lodash';

import { useDispensaryChainsLazy } from 'src/dispensary/hooks/use-dispensary-chain';
import useErnie from 'shared/hooks/use-ernie';
import useTranslation from 'hooks/use-translation';
import { getNearbyDispensariesSortedByDistance } from 'src/utils/geo/nearby-dispensaries';

import { getOpenDispensary, getAfterHoursDispensary } from './locations.utils';

export function useNearestDispensaryLocation(searchByDeliveryArea) {
  const showErnie = useErnie();

  const { t } = useTranslation();

  const { dispensaryChainLocations, getDispensaryChain, error, loading } = useDispensaryChainsLazy();

  const nearestDispensaryLocation = useMemo(() => {
    if (!dispensaryChainLocations) {
      return undefined;
    }

    if (!dispensaryChainLocations.length) {
      showErnie(`We're sorry, this address falls outside our delivery area.`, `danger`);
      return undefined;
    }

    if (!searchByDeliveryArea) {
      return _.get(dispensaryChainLocations, '[0]');
    }

    const nearestLocation =
      getOpenDispensary(dispensaryChainLocations) || getAfterHoursDispensary(dispensaryChainLocations);

    if (!nearestLocation) {
      showErnie(
        `We're sorry, the dispensaries in your delivery area are currently closed for online ordering.`,
        `danger`
      );
      return undefined;
    }
    return nearestLocation;
  }, [dispensaryChainLocations, searchByDeliveryArea, showErnie]);

  useEffect(() => {
    if (error) {
      showErnie(t('locations.error', 'Something went wrong. Please contact support.'), `danger`);
    }
  }, [error, showErnie, t]);

  // All chain locations within the nearby radius, closest first -- used to list alternatives
  // instead of forcing the visitor into a single auto-picked store. Delivery-area search still
  // auto-routes to one match, so this list only applies to the pickup/menu locator flow.
  const nearbyDispensaryLocations = useMemo(() => {
    if (searchByDeliveryArea || !dispensaryChainLocations) {
      return [];
    }
    return getNearbyDispensariesSortedByDistance(dispensaryChainLocations);
  }, [dispensaryChainLocations, searchByDeliveryArea]);

  return {
    nearestDispensaryLocation,
    nearbyDispensaryLocations,
    getNearestDispensaryLocation: getDispensaryChain,
    error,
    loading,
  };
}
